import { getDatasetControlAggregateActionApiRequestData } from '../../../../api/utils/requestData/getDatasetControlAggregateActionApiRequestData';
import { Dataset, DatasetControl, JSONObject } from '../../../../types/types';
import { ResponseActionHandler } from '../../../../types/context';
import { getFilterParamObject } from '../../../../utils/apiRequests/getFilterParamObject';
import { getSelectedRowIndexesWithVirtual } from '../../../../utils/apiRequests/getSelectedRowIndexesWithVirtual';
import { getRowId } from '../../../../utils/getRowId';
import { probeRowIndexesByIds } from '../../../../utils/dataset/probeRowIndexesByIds';
import useApiErrorHandling from '../../../useApiErrorHandling';
import { getAggregateApiCallback } from '../getAggregateApiCallback';
import { AggregateResultItem } from '../../../../components/Modals/AggregateResultModalContent/types';

export const getAggregateTypeCallback = (
  dataset: Dataset,
  control: DatasetControl,
  token: string | null,
  activeFilters: Record<string, unknown> | null,
  activeSorting: { field: string; order: number }[],
  data: any[],
  selectedRows: any[],
  selectedVirtualRows: number,
  deselectedRowIds: Set<string>,
  isSlaveDataset: boolean,
  masterExpandedRow: any | undefined,
  masterDataKey: string,
  cachedQueryId: string,
  handleApiError: ReturnType<typeof useApiErrorHandling>,
  getResponseActionHandler?: (
    name: string
  ) => ResponseActionHandler | undefined,
  onResult?: (items: AggregateResultItem[]) => Promise<void> | void,
  // Grid loading + disabled controls for the whole run (probe + aggregate).
  setProcessingLoading?: (value: boolean) => void,
  datasetControl?: JSONObject
) => {
  return async () => {
    setProcessingLoading?.(true);
    try {
      let indexes = getSelectedRowIndexesWithVirtual(
        data,
        selectedRows,
        selectedVirtualRows
      );

      const probeMasterRowId =
        isSlaveDataset && masterExpandedRow
          ? String(masterExpandedRow[masterDataKey])
          : undefined;

      // Select-all minus a few: the unchecked rows are the only ones we know by
      // id, so probe those and take everything else. Exact even when a sort has
      // moved them out of the loaded window, which the index math cannot be.
      if (deselectedRowIds.size > 0 && dataset.idItem) {
        const probeResult = await probeRowIndexesByIds({
          dataset,
          activeFilters,
          activeSorting,
          sessionToken: token,
          lockedCachedQueryId: cachedQueryId,
          itemIds: Array.from(deselectedRowIds),
          masterRowId: probeMasterRowId,
          datasetControl,
        });
        if (probeResult) {
          const excludedIndexes = new Set<number>(probeResult.indexes);
          const allIndexes: number[] = [];
          for (let index = 0; index < probeResult.totalRowCount; index += 1) {
            if (!excludedIndexes.has(index)) allIndexes.push(index);
          }
          indexes = allIndexes;
        }
      }

      // A sort/filter change can leave checked rows outside the loaded window,
      // so their indexes can't be computed from loaded data. Resolve them with
      // an id-only probe (just the id column — cheap even for large datasets)
      // instead of fetching the rows. Probe failure keeps the plain indexes.
      if (
        selectedVirtualRows === 0 &&
        deselectedRowIds.size === 0 &&
        dataset.idItem &&
        indexes.length < selectedRows.length
      ) {
        const selectedIds = selectedRows
          .map((row) => getRowId(row, dataset.idItem as string))
          .filter((id): id is string => id !== null);
        const probeResult = await probeRowIndexesByIds({
          dataset,
          activeFilters,
          activeSorting,
          sessionToken: token,
          lockedCachedQueryId: cachedQueryId,
          itemIds: selectedIds,
          masterRowId: probeMasterRowId,
          datasetControl,
        });
        if (probeResult) {
          indexes = probeResult.indexes;
        }
      }

      const filters = getFilterParamObject(activeFilters || {});

      const master =
        isSlaveDataset && masterExpandedRow
          ? { id: masterExpandedRow[masterDataKey], idItem: masterDataKey }
          : undefined;

      // Set only when actually held — null drops the x-cachedquery-id header
      const requestData = getDatasetControlAggregateActionApiRequestData(
        dataset,
        control,
        token,
        activeSorting,
        filters,
        indexes,
        master,
        cachedQueryId || null,
        datasetControl
      );

      const callback = getAggregateApiCallback(
        control,
        token,
        requestData,
        handleApiError,
        getResponseActionHandler
      );

      const result = await callback();
      // Awaited so the grid stays loading while onResult resolves the number format
      if (Array.isArray(result)) await onResult?.(result);
    } finally {
      // Always release — errors propagate, loading must not stick
      setProcessingLoading?.(false);
    }
  };
};
